'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PARAMETERIZED_MODULES } from './mxp365-v2.types';
import { MxpNavigationError } from './mxp-navigation-error';

import type {
  ModuleReadyV2Payload,
  MxpModuleIdV2,
  MxpV2ErrorCode,
  MxpV2State,
  NavigateToV2Message,
  UseMxpIframeOptions,
  UseMxpIframeReturn,
} from './mxp365-v2.types';

type PendingRequest = {
  requestId: string;
  module: MxpModuleIdV2;
  parameter: string | null;
  startedAt: number;
  timeoutHandle: ReturnType<typeof setTimeout>;
  resolve: (payload: ModuleReadyV2Payload) => void;
  reject: (err: MxpNavigationError) => void;
};

const DEFAULT_TIMEOUT_MS = 10_000;

const generateRequestId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const parseJsonPayload = (
  raw: unknown,
): Record<string, string | undefined> | undefined => {
  if (typeof raw !== 'string') {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, string | undefined>;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

export function useMxpIframe(options: UseMxpIframeOptions): UseMxpIframeReturn {
  const {
    src,
    targetOrigin,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    onEvent,
    onReady,
    onShare,
    onFavoriteAdded,
    onHeightChange,
    onNavigate,
    onTitleChange,
    onJwtLoginError,
  } = options;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const pendingRef = useRef<Map<string, PendingRequest>>(new Map());
  const readyFiredRef = useRef(false);

  // Hold callbacks in refs so changing them does not re-register the listener.
  const callbacksRef = useRef({
    onEvent,
    onReady,
    onShare,
    onFavoriteAdded,
    onHeightChange,
    onNavigate,
    onTitleChange,
    onJwtLoginError,
  });
  useEffect(() => {
    callbacksRef.current = {
      onEvent,
      onReady,
      onShare,
      onFavoriteAdded,
      onHeightChange,
      onNavigate,
      onTitleChange,
      onJwtLoginError,
    };
  }, [
    onEvent,
    onReady,
    onShare,
    onFavoriteAdded,
    onHeightChange,
    onNavigate,
    onTitleChange,
    onJwtLoginError,
  ]);

  const [state, setState] = useState<MxpV2State>({
    ready: false,
    currentUrl: null,
    lastEvent: null,
    navigating: false,
  });

  const updateNavigatingFlag = useCallback(() => {
    setState(prev => {
      const navigating = pendingRef.current.size > 0;
      if (prev.navigating === navigating) return prev;
      return { ...prev, navigating };
    });
  }, []);

  useEffect(() => {
    function handleMessage(event: MessageEvent): void {
      if (event.origin !== targetOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const data = event.data as
        | { action?: string; payload?: unknown }
        | undefined;
      if (!data || typeof data.action !== 'string') return;

      const action = data.action;
      const payload = data.payload as Record<string, unknown> | undefined;

      if (action === 'moduleReadyV2' && payload) {
        const ready = payload as unknown as ModuleReadyV2Payload;
        const pending = pendingRef.current.get(ready.requestId);

        if (pending) {
          clearTimeout(pending.timeoutHandle);
          pendingRef.current.delete(ready.requestId);

          if (ready.status === 'error') {
            const code: MxpV2ErrorCode = ready.errorCode ?? 'NAVIGATION_FAILED';
            pending.reject(
              new MxpNavigationError(
                code,
                ready.errorMessage ?? 'Navigation failed',
                ready.requestId,
              ),
            );
          } else {
            pending.resolve(ready);
          }
        }

        setState(() => ({
          ready: true,
          currentUrl: ready.currentUrl,
          lastEvent: ready,
          navigating: pendingRef.current.size > 0,
        }));

        callbacksRef.current.onEvent?.(ready);
        if (!readyFiredRef.current) {
          performance.mark('mxp:v2:ready');
          readyFiredRef.current = true;
          callbacksRef.current.onReady?.(ready);
        }
        return;
      }

      switch (action) {
        case 'share':
          callbacksRef.current.onShare?.(
            payload as { url: string; title?: string; text?: string },
          );
          return;
        case 'favoriteAdded':
          callbacksRef.current.onFavoriteAdded?.();
          return;
        case 'windowHeight':
          callbacksRef.current.onHeightChange?.(payload as { height: number });
          return;
        case 'navigate':
          callbacksRef.current.onNavigate?.(
            payload as { allowedNavigateBack: boolean },
          );
          return;
        case 'navigationChanged':
          callbacksRef.current.onTitleChange?.(
            payload as { locationUri: string },
          );
          return;
        case 'jwtLoginError':
          callbacksRef.current.onJwtLoginError?.({
            payload: parseJsonPayload(payload),
          });
          return;
        default:
          return;
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [targetOrigin]);

  useEffect(() => {
    const pending = pendingRef.current;
    return () => {
      pending.forEach(req => {
        clearTimeout(req.timeoutHandle);
        req.reject(
          new MxpNavigationError(
            'CLIENT_TIMEOUT',
            'Component unmounted before response',
            req.requestId,
          ),
        );
      });
      pending.clear();
    };
  }, []);

  const postMessage = useCallback<UseMxpIframeReturn['postMessage']>(
    message => {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      try {
        win.postMessage(message, targetOrigin);
      } catch {
        // Cross-origin postMessage failures are not actionable here.
      }
    },
    [targetOrigin],
  );

  const navigate = useCallback<UseMxpIframeReturn['navigate']>(
    (module, parameter = null) => {
      return new Promise<ModuleReadyV2Payload>((resolve, reject) => {
        const win = iframeRef.current?.contentWindow;
        if (!win) {
          reject(
            new MxpNavigationError(
              'NAVIGATION_FAILED',
              'Iframe is not mounted yet',
            ),
          );
          return;
        }

        if (PARAMETERIZED_MODULES.has(module) && !parameter) {
          reject(
            new MxpNavigationError(
              'MODULE_NOT_FOUND',
              `Module "${module}" requires a parameter`,
            ),
          );
          return;
        }

        const requestId = generateRequestId();

        const timeoutHandle = setTimeout(() => {
          pendingRef.current.delete(requestId);
          updateNavigatingFlag();
          reject(
            new MxpNavigationError(
              'CLIENT_TIMEOUT',
              `No moduleReadyV2 received within ${timeoutMs}ms`,
              requestId,
            ),
          );
        }, timeoutMs);

        pendingRef.current.set(requestId, {
          requestId,
          module,
          parameter,
          startedAt: Date.now(),
          timeoutHandle,
          resolve: payload => {
            updateNavigatingFlag();
            resolve(payload);
          },
          reject,
        });

        setState(prev => ({ ...prev, navigating: true }));

        const message: NavigateToV2Message = {
          action: 'navigateToV2',
          payload: { mxpModule: module, mxpParameter: parameter, requestId },
        };

        try {
          win.postMessage(message, targetOrigin);
        } catch (e) {
          clearTimeout(timeoutHandle);
          pendingRef.current.delete(requestId);
          updateNavigatingFlag();
          reject(
            new MxpNavigationError(
              'NAVIGATION_FAILED',
              e instanceof Error ? e.message : 'postMessage failed',
              requestId,
            ),
          );
        }
      });
    },
    [targetOrigin, timeoutMs, updateNavigatingFlag],
  );

  const reload = useCallback(() => {
    pendingRef.current.forEach(req => {
      clearTimeout(req.timeoutHandle);
      req.reject(
        new MxpNavigationError(
          'CLIENT_TIMEOUT',
          'Iframe was reloaded',
          req.requestId,
        ),
      );
    });
    pendingRef.current.clear();
    readyFiredRef.current = false;

    setState({
      ready: false,
      currentUrl: null,
      lastEvent: null,
      navigating: false,
    });

    const iframe = iframeRef.current;
    if (!iframe) return;

    iframe.src = 'about:blank';
    window.setTimeout(() => {
      if (iframeRef.current) iframeRef.current.src = src;
    }, 100);
  }, [src]);

  return useMemo(
    () => ({ iframeRef, state, navigate, reload, postMessage }),
    [state, navigate, reload, postMessage],
  );
}
